// Vendored, dependency-free, network-free QR Code encoder (pure TypeScript).
//
// Why vendored: the enrollment panel renders an otpauth:// QR client-side under a
// strict CSP (no external fetch, no CDN, artifact-safe). Rather than pull the
// `qrcode` npm package + its transitive deps, this is a compact byte-mode encoder
// sufficient for otpauth URIs (~120-180 bytes). It supports:
//   - Byte (8-bit) encoding mode only (covers the full otpauth URI charset).
//   - Error-correction level M.
//   - Automatic version selection (1..40) by capacity.
//   - Reed-Solomon ECC, all 8 data masks with penalty-based selection.
//   - Format + version information.
//
// Output is a square boolean matrix (true = dark module), quiet zone NOT included
// (the SVG renderer adds it). Algorithms follow the QR Code spec (ISO/IEC 18004).

// Galois field GF(256) tables for Reed-Solomon (generator 0x11d).

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function initGaloisField() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) {
    GF_EXP[i] = GF_EXP[i - 255];
  }
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return GF_EXP[GF_LOG[a] + GF_LOG[b]];
}

/**
 * Reed-Solomon generator polynomial of the given degree, returned
 * leading-coefficient-first: [1, g_{d-1}, ..., g_0]. Length = degree + 1.
 * It is the product (x - α^0)(x - α^1)...(x - α^{degree-1}).
 */
function rsGeneratorPoly(degree: number): number[] {
  // poly[0] is the highest-degree coefficient. Start with the monomial x^0 = [1].
  let poly = [1];
  for (let i = 0; i < degree; i++) {
    // Multiply poly by (x - α^i) == (x + α^i) in GF(2^m).
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j]; // x * poly[j] shifts up one degree.
      next[j + 1] ^= gfMul(poly[j], GF_EXP[i]); // + α^i * poly[j]
    }
    poly = next;
  }
  return poly;
}

/** Compute the `ecLen` Reed-Solomon codewords for a data codeword block. */
function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGeneratorPoly(ecLen); // leading-first, length ecLen+1, gen[0]===1.
  const res = new Array<number>(ecLen).fill(0);
  for (const byte of data) {
    const factor = byte ^ res[0];
    res.shift();
    res.push(0);
    if (factor !== 0) {
      // Subtract factor * gen (excluding the leading gen[0] === 1 term).
      for (let i = 0; i < ecLen; i++) {
        res[i] ^= gfMul(gen[i + 1], factor);
      }
    }
  }
  return res;
}

// Capacity + ECC block tables for error-correction level M, versions 1..40
// (dataCodewords = total data codewords; ecPerBlock = EC codewords/block; g1/g2 = block group sizes).

interface VersionEC {
  ecPerBlock: number;
  g1Blocks: number;
  g1Words: number;
  g2Blocks: number;
  g2Words: number;
}

// Level M tables (ISO/IEC 18004, Table 9). Index by version (1-based).
const EC_M: Record<number, VersionEC> = {
  1: { ecPerBlock: 10, g1Blocks: 1, g1Words: 16, g2Blocks: 0, g2Words: 0 },
  2: { ecPerBlock: 16, g1Blocks: 1, g1Words: 28, g2Blocks: 0, g2Words: 0 },
  3: { ecPerBlock: 26, g1Blocks: 1, g1Words: 44, g2Blocks: 0, g2Words: 0 },
  4: { ecPerBlock: 18, g1Blocks: 2, g1Words: 32, g2Blocks: 0, g2Words: 0 },
  5: { ecPerBlock: 24, g1Blocks: 2, g1Words: 43, g2Blocks: 0, g2Words: 0 },
  6: { ecPerBlock: 16, g1Blocks: 4, g1Words: 27, g2Blocks: 0, g2Words: 0 },
  7: { ecPerBlock: 18, g1Blocks: 4, g1Words: 31, g2Blocks: 0, g2Words: 0 },
  8: { ecPerBlock: 22, g1Blocks: 2, g1Words: 38, g2Blocks: 2, g2Words: 39 },
  9: { ecPerBlock: 22, g1Blocks: 3, g1Words: 36, g2Blocks: 2, g2Words: 37 },
  10: { ecPerBlock: 26, g1Blocks: 4, g1Words: 43, g2Blocks: 1, g2Words: 44 },
  11: { ecPerBlock: 30, g1Blocks: 1, g1Words: 50, g2Blocks: 4, g2Words: 51 },
  12: { ecPerBlock: 22, g1Blocks: 6, g1Words: 36, g2Blocks: 2, g2Words: 37 },
  13: { ecPerBlock: 22, g1Blocks: 8, g1Words: 37, g2Blocks: 1, g2Words: 38 },
  14: { ecPerBlock: 24, g1Blocks: 4, g1Words: 40, g2Blocks: 5, g2Words: 41 },
  15: { ecPerBlock: 24, g1Blocks: 5, g1Words: 41, g2Blocks: 5, g2Words: 42 },
  16: { ecPerBlock: 28, g1Blocks: 7, g1Words: 45, g2Blocks: 3, g2Words: 46 },
  17: { ecPerBlock: 28, g1Blocks: 10, g1Words: 46, g2Blocks: 1, g2Words: 47 },
  18: { ecPerBlock: 26, g1Blocks: 9, g1Words: 43, g2Blocks: 4, g2Words: 44 },
  19: { ecPerBlock: 26, g1Blocks: 3, g1Words: 44, g2Blocks: 11, g2Words: 45 },
  20: { ecPerBlock: 26, g1Blocks: 3, g1Words: 41, g2Blocks: 13, g2Words: 42 },
};

/** Total data codewords available at level M for a version. */
function dataCodewords(v: VersionEC): number {
  return v.g1Blocks * v.g1Words + v.g2Blocks * v.g2Words;
}

// Alignment pattern center coordinates by version (ISO/IEC 18004 Annex E).

const ALIGNMENT_POSITIONS: Record<number, number[]> = {
  1: [],
  2: [6, 18],
  3: [6, 22],
  4: [6, 26],
  5: [6, 30],
  6: [6, 34],
  7: [6, 22, 38],
  8: [6, 24, 42],
  9: [6, 26, 46],
  10: [6, 28, 50],
  11: [6, 30, 54],
  12: [6, 32, 58],
  13: [6, 34, 62],
  14: [6, 26, 46, 66],
  15: [6, 26, 48, 70],
  16: [6, 26, 50, 74],
  17: [6, 30, 54, 78],
  18: [6, 30, 56, 82],
  19: [6, 30, 58, 86],
  20: [6, 34, 62, 90],
};

class BitBuffer {
  readonly bits: number[] = [];
  put(value: number, length: number): void {
    for (let i = length - 1; i >= 0; i--) {
      this.bits.push((value >>> i) & 1);
    }
  }
  get length(): number {
    return this.bits.length;
  }
}

/** UTF-8 encode a string into a byte array (browser + jsdom safe). */
function toUtf8Bytes(s: string): number[] {
  // Fall back to a manual encoder only if TextEncoder is unavailable.
  if (typeof TextEncoder !== 'undefined') {
    return Array.from(new TextEncoder().encode(s));
  }
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) {
    let c = s.charCodeAt(i);
    if (c < 0x80) out.push(c);
    else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else {
      out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return out;
}

/** Pick the smallest supported version whose data capacity fits the payload. */
function selectVersion(byteLen: number): number {
  for (let version = 1; version <= 20; version++) {
    const ec = EC_M[version];
    const capacityBits = dataCodewords(ec) * 8;
    // Mode indicator (4) + char count (8 or 16) + data (8*len).
    const charCountBits = version < 10 ? 8 : 16;
    const needed = 4 + charCountBits + byteLen * 8;
    if (needed <= capacityBits) return version;
  }
  throw new Error('QR payload too large for supported versions (<= 20)');
}

/** Build the final interleaved codeword stream (data + EC) for a version. */
function buildCodewords(dataBytes: number[], version: number): number[] {
  const ec = EC_M[version];
  const charCountBits = version < 10 ? 8 : 16;

  const buf = new BitBuffer();
  buf.put(0b0100, 4); // Byte mode indicator.
  buf.put(dataBytes.length, charCountBits);
  for (const b of dataBytes) buf.put(b, 8);

  const totalDataCodewords = dataCodewords(ec);
  const capacityBits = totalDataCodewords * 8;

  // Terminator (up to 4 zero bits) + pad to a byte boundary.
  const terminator = Math.min(4, capacityBits - buf.length);
  buf.put(0, terminator);
  while (buf.length % 8 !== 0) buf.bits.push(0);

  const codewords: number[] = [];
  for (let i = 0; i < buf.length; i += 8) {
    let byte = 0;
    for (let j = 0; j < 8; j++) byte = (byte << 1) | buf.bits[i + j];
    codewords.push(byte);
  }
  // Pad codewords with the alternating pad bytes.
  const PAD = [0xec, 0x11];
  let padIdx = 0;
  while (codewords.length < totalDataCodewords) {
    codewords.push(PAD[padIdx % 2]);
    padIdx++;
  }

  // Split into blocks.
  const blocks: number[][] = [];
  const ecBlocks: number[][] = [];
  let pos = 0;
  for (let b = 0; b < ec.g1Blocks; b++) {
    const block = codewords.slice(pos, pos + ec.g1Words);
    pos += ec.g1Words;
    blocks.push(block);
    ecBlocks.push(rsEncode(block, ec.ecPerBlock));
  }
  for (let b = 0; b < ec.g2Blocks; b++) {
    const block = codewords.slice(pos, pos + ec.g2Words);
    pos += ec.g2Words;
    blocks.push(block);
    ecBlocks.push(rsEncode(block, ec.ecPerBlock));
  }

  // Interleave data codewords, then EC codewords.
  const result: number[] = [];
  const maxData = Math.max(...blocks.map((b) => b.length));
  for (let i = 0; i < maxData; i++) {
    for (const block of blocks) {
      if (i < block.length) result.push(block[i]);
    }
  }
  for (let i = 0; i < ec.ecPerBlock; i++) {
    for (const eb of ecBlocks) {
      result.push(eb[i]);
    }
  }
  return result;
}

type Cell = boolean | null; // null = not yet placed (used to track function modules).

function sizeForVersion(version: number): number {
  return 17 + version * 4;
}

function placeFinder(matrix: Cell[][], reserved: boolean[][], row: number, col: number): void {
  for (let r = -1; r <= 7; r++) {
    for (let c = -1; c <= 7; c++) {
      const rr = row + r;
      const cc = col + c;
      if (rr < 0 || cc < 0 || rr >= matrix.length || cc >= matrix.length) continue;
      reserved[rr][cc] = true;
      const isBorder = r === 0 || r === 6 || c === 0 || c === 6;
      const isCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4;
      const inFinder = r >= 0 && r <= 6 && c >= 0 && c <= 6;
      matrix[rr][cc] = inFinder && (isBorder || isCenter);
    }
  }
}

function placeAlignment(matrix: Cell[][], reserved: boolean[][], version: number): void {
  const positions = ALIGNMENT_POSITIONS[version];
  for (const r of positions) {
    for (const c of positions) {
      // Skip where it would overlap a finder pattern.
      if ((r === 6 && c === 6) || (r === 6 && c === positions[positions.length - 1]) ||
          (r === positions[positions.length - 1] && c === 6)) {
        continue;
      }
      if (reserved[r][c]) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          reserved[r + dr][c + dc] = true;
          const ring = Math.max(Math.abs(dr), Math.abs(dc));
          matrix[r + dr][c + dc] = ring === 2 || ring === 0;
        }
      }
    }
  }
}

function placeTiming(matrix: Cell[][], reserved: boolean[][]): void {
  const n = matrix.length;
  for (let i = 8; i < n - 8; i++) {
    const dark = i % 2 === 0;
    if (!reserved[6][i]) {
      matrix[6][i] = dark;
      reserved[6][i] = true;
    }
    if (!reserved[i][6]) {
      matrix[i][6] = dark;
      reserved[i][6] = true;
    }
  }
}

function reserveFormatAreas(matrix: Cell[][], reserved: boolean[][], version: number): void {
  const n = matrix.length;
  // Format info areas around the top-left, plus strips by TR and BL finders.
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      reserved[8][i] = true;
      reserved[i][8] = true;
    }
  }
  for (let i = 0; i < 8; i++) {
    reserved[8][n - 1 - i] = true;
    reserved[n - 1 - i][8] = true;
  }
  // Dark module.
  matrix[n - 8][8] = true;
  reserved[n - 8][8] = true;

  // Version info areas (version >= 7).
  if (version >= 7) {
    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 3; j++) {
        reserved[i][n - 11 + j] = true;
        reserved[n - 11 + j][i] = true;
      }
    }
  }
}

function placeData(matrix: Cell[][], reserved: boolean[][], codewords: number[]): void {
  const n = matrix.length;
  const bits: number[] = [];
  for (const cw of codewords) {
    for (let i = 7; i >= 0; i--) bits.push((cw >> i) & 1);
  }

  let bitIdx = 0;
  let upward = true;
  for (let col = n - 1; col > 0; col -= 2) {
    if (col === 6) col--; // Skip the vertical timing column.
    for (let i = 0; i < n; i++) {
      const row = upward ? n - 1 - i : i;
      for (let c = 0; c < 2; c++) {
        const cc = col - c;
        if (reserved[row][cc]) continue;
        const bit = bitIdx < bits.length ? bits[bitIdx] : 0;
        matrix[row][cc] = bit === 1;
        bitIdx++;
      }
    }
    upward = !upward;
  }
}

const MASKS: Array<(r: number, c: number) => boolean> = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

function applyMask(matrix: Cell[][], reserved: boolean[][], maskId: number): boolean[][] {
  const n = matrix.length;
  const out: boolean[][] = matrix.map((row) => row.map((v) => v === true));
  const mask = MASKS[maskId];
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      if (!reserved[r][c] && mask(r, c)) {
        out[r][c] = !out[r][c];
      }
    }
  }
  return out;
}

// BCH-encoded format info for level M + mask, XORed with the spec mask 0x5412.
function formatBits(maskId: number): number {
  // EC level M = 0b00.
  const data = (0b00 << 3) | maskId;
  let bch = data << 10;
  const g = 0b10100110111;
  for (let i = 14; i >= 10; i--) {
    if ((bch >> i) & 1) bch ^= g << (i - 10);
  }
  return ((data << 10) | (bch & 0x3ff)) ^ 0x5412;
}

function placeFormat(matrix: boolean[][], maskId: number): void {
  const n = matrix.length;
  const fmt = formatBits(maskId);
  // bit(i) = i-th format bit, spec-numbered from the MSB (i=0 is bit 14 .. i=14 is bit 0).
  const bit = (i: number) => ((fmt >> (14 - i)) & 1) === 1;

  // Copy 1 (around the top-left finder): the 15 bits wrap the finder corner.
  // Horizontal strip along row 8 (skipping the timing column at col 6).
  for (let i = 0; i <= 5; i++) matrix[8][i] = bit(i);
  matrix[8][7] = bit(6);
  matrix[8][8] = bit(7);
  matrix[7][8] = bit(8);
  // Vertical strip up column 8 (skipping the timing row at row 6).
  for (let i = 9; i <= 14; i++) matrix[14 - i][8] = bit(i);

  // Copy 2 (split across the TR and BL finders).
  // First 7 bits go up column 8 from the bottom-left finder (rows n-1..n-7).
  for (let i = 0; i <= 6; i++) matrix[n - 1 - i][8] = bit(i);
  // Remaining 8 bits run along row 8 from the top-right finder (cols n-8..n-1).
  for (let i = 7; i <= 14; i++) matrix[8][n - 15 + i] = bit(i);

  // Mandatory dark module (never part of format info).
  matrix[n - 8][8] = true;
}

// Version info (versions >= 7) BCH(18,6) with golay generator.
function versionBits(version: number): number {
  let bch = version << 12;
  const g = 0b1111100100101;
  for (let i = 17; i >= 12; i--) {
    if ((bch >> i) & 1) bch ^= g << (i - 12);
  }
  return (version << 12) | (bch & 0xfff);
}

function placeVersion(matrix: boolean[][], version: number): void {
  if (version < 7) return;
  const n = matrix.length;
  const vb = versionBits(version);
  for (let i = 0; i < 18; i++) {
    const bit = ((vb >> i) & 1) === 1;
    const r = Math.floor(i / 3);
    const c = i % 3;
    matrix[r][n - 11 + c] = bit;
    matrix[n - 11 + c][r] = bit;
  }
}

// Mask penalty scoring (ISO/IEC 18004 §8.8.2), used to select the best mask.
function penalty(matrix: boolean[][]): number {
  const n = matrix.length;
  let score = 0;

  // Rule 1: runs of 5+ same-color modules in rows and columns.
  for (let r = 0; r < n; r++) {
    let runColor = matrix[r][0];
    let run = 1;
    for (let c = 1; c < n; c++) {
      if (matrix[r][c] === runColor) run++;
      else {
        if (run >= 5) score += run - 2;
        runColor = matrix[r][c];
        run = 1;
      }
    }
    if (run >= 5) score += run - 2;
  }
  for (let c = 0; c < n; c++) {
    let runColor = matrix[0][c];
    let run = 1;
    for (let r = 1; r < n; r++) {
      if (matrix[r][c] === runColor) run++;
      else {
        if (run >= 5) score += run - 2;
        runColor = matrix[r][c];
        run = 1;
      }
    }
    if (run >= 5) score += run - 2;
  }

  // Rule 2: 2x2 blocks of the same color.
  for (let r = 0; r < n - 1; r++) {
    for (let c = 0; c < n - 1; c++) {
      const v = matrix[r][c];
      if (v === matrix[r][c + 1] && v === matrix[r + 1][c] && v === matrix[r + 1][c + 1]) {
        score += 3;
      }
    }
  }

  // Rule 3: finder-like 1:1:3:1:1 patterns in rows/cols.
  const pattern1 = [true, false, true, true, true, false, true, false, false, false, false];
  const pattern2 = [false, false, false, false, true, false, true, true, true, false, true];
  const matches = (arr: boolean[], off: number, pat: boolean[]) => {
    for (let k = 0; k < pat.length; k++) if (arr[off + k] !== pat[k]) return false;
    return true;
  };
  for (let r = 0; r < n; r++) {
    for (let c = 0; c <= n - 11; c++) {
      const rowSlice = matrix[r];
      if (matches(rowSlice, c, pattern1) || matches(rowSlice, c, pattern2)) score += 40;
    }
  }
  for (let c = 0; c < n; c++) {
    const colSlice = matrix.map((row) => row[c]);
    for (let r = 0; r <= n - 11; r++) {
      if (matches(colSlice, r, pattern1) || matches(colSlice, r, pattern2)) score += 40;
    }
  }

  // Rule 4: proportion of dark modules.
  let dark = 0;
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (matrix[r][c]) dark++;
  const percent = (dark * 100) / (n * n);
  const prev = Math.floor(percent / 5) * 5;
  score += Math.min(Math.abs(prev - 50), Math.abs(prev + 5 - 50)) / 5 * 10;

  return score;
}

/**
 * Encode a string into a QR Code module matrix (byte mode, EC level M).
 * @returns a square boolean[][] where true = dark module. Quiet zone excluded.
 */
export function encodeQr(text: string): boolean[][] {
  const dataBytes = toUtf8Bytes(text);
  const version = selectVersion(dataBytes.length);
  const codewords = buildCodewords(dataBytes, version);
  const n = sizeForVersion(version);

  // Base matrix with function patterns.
  const base: Cell[][] = Array.from({ length: n }, () => new Array<Cell>(n).fill(null));
  const reserved: boolean[][] = Array.from({ length: n }, () => new Array<boolean>(n).fill(false));

  placeFinder(base, reserved, 0, 0);
  placeFinder(base, reserved, 0, n - 7);
  placeFinder(base, reserved, n - 7, 0);
  placeAlignment(base, reserved, version);
  placeTiming(base, reserved);
  reserveFormatAreas(base, reserved, version);
  placeData(base, reserved, codewords);

  // Try all masks, keep the lowest penalty.
  let best: boolean[][] | null = null;
  let bestScore = Infinity;
  for (let maskId = 0; maskId < 8; maskId++) {
    const masked = applyMask(base, reserved, maskId);
    placeFormat(masked, maskId);
    placeVersion(masked, version);
    const score = penalty(masked);
    if (score < bestScore) {
      bestScore = score;
      best = masked;
    }
  }
  return best!;
}
